import React, { useState, useEffect } from 'react';
import { Form, Button, Space, message, Input, Radio, Upload, Row, Col, Card, Typography, Divider, Alert } from 'antd';
import { UploadOutlined, EyeOutlined, LinkOutlined, FileOutlined } from '@ant-design/icons';
import { getParametersForImagingStudy } from '../utils/imagingParameters';
import ImagingLanguageToggle from './ImagingLanguageToggle';
import { useImagingLanguage } from '../hooks/useImagingLanguage';

const { Title, Text } = Typography;

const EKGStudyInputForm = ({ patientId, onSave, onCancel, initialData }) => {
  const [form] = Form.useForm();
  const [parameters, setParameters] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [documentType, setDocumentType] = useState('file');
  const [externalLink, setExternalLink] = useState('');
  const [existingDocument, setExistingDocument] = useState(null);

  // Language management hook
  const {
    currentLanguage,
    changeLanguage,
    getTranslatedParameterName,
    getTranslatedOption,
    getTranslatedOptions,
    getTranslatedPlaceholder,
    isVietnamese
  } = useImagingLanguage();

  useEffect(() => {
    // Get EKG parameters
    const ekgParams = getParametersForImagingStudy('EKG');
    setParameters(ekgParams);
  }, []);

  useEffect(() => {
    // Populate form with initial data when editing
    if (initialData) {
      console.log('EKG Form - Initial data received:', initialData);

      // Populate EKG parameter fields
      if (initialData.details && Array.isArray(initialData.details)) {
        const formValues = {};
        initialData.details.forEach(detail => {
          if (detail.name) {
            formValues[detail.name] = detail.value;
          }
        });
        form.setFieldsValue(formValues);
        console.log('EKG Form - Set form values:', formValues);
      }

      // Handle existing document information
      if (initialData.document) {
        setExistingDocument(initialData.document);
        if (initialData.document.documentType === 'link') {
          setDocumentType('link');
          setExternalLink(initialData.document.externalLink || '');
        } else {
          setDocumentType('file');
        }
      }
    }
  }, [initialData, form]);

  const handleFinish = (values) => {
    // Filter out any parameters that were not filled
    const filledValues = {};
    for (const key in values) {
      if (values[key] !== undefined && values[key] !== null) {
        if (typeof values[key] === 'string' && values[key].trim() === '') {
          continue; // Skip empty strings
        }
        filledValues[key] = values[key];
      }
    }

    // Check if we have either file/link or form data
    const hasFile = fileList.length > 0;
    const hasLink = documentType === 'link' && externalLink.trim() !== '';
    const hasFindings = Object.keys(filledValues).length > 0;

    if (!hasFindings && !hasFile && !hasLink) {
      message.warning('Please upload an EKG file, provide an external link, or enter at least one finding.');
      return;
    }

    // Validate external link if provided
    if (documentType === 'link' && externalLink.trim() !== '') {
      try {
        new URL(externalLink.trim());
      } catch (error) {
        message.error('Please enter a valid URL for the external link.');
        return;
      }
    }

    const findingsArray = Object.entries(filledValues).map(([key, value]) => {
      const paramDefinition = parameters.find(p => p.key === key);
      return {
        key: key,
        name: paramDefinition ? paramDefinition.name : key,
        value: value,
      };
    });

    // Include document information
    const submissionData = {
      findings: findingsArray,
      studyType: 'EKG',
      documentType: documentType,
      file: hasFile ? fileList[0] : null,
      externalLink: hasLink ? externalLink.trim() : null,
      existingDocumentId: existingDocument?._id || null
    };

    console.log('Submitting EKG Study Data:', submissionData);
    onSave(submissionData);
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
      setUploadedFile(null);
    },
    beforeUpload: (file) => {
      // Validate file type
      const isValidType = file.type === 'application/pdf' || 
                         file.type.startsWith('image/') ||
                         file.name.toLowerCase().endsWith('.dcm');
      
      if (!isValidType) {
        message.error('Please upload a PDF, image file (JPEG, PNG), or DICOM file.');
        return false;
      }

      // Validate file size (10MB limit)
      const isValidSize = file.size / 1024 / 1024 < 10;
      if (!isValidSize) {
        message.error('File must be smaller than 10MB.');
        return false;
      }

      setFileList([file]);
      setUploadedFile(file);
      return false; // Prevent auto-upload
    },
    fileList,
    maxCount: 1,
    accept: '.pdf,.jpg,.jpeg,.png,.dcm',
  };

  const handlePreview = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      window.open(url, '_blank');
    }
  };

  const handleDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    if (e.target.value === 'link') {
      setFileList([]);
      setUploadedFile(null);
    } else {
      setExternalLink('');
    }
  };

  const handleExternalLinkChange = (e) => {
    setExternalLink(e.target.value);
  };

  const handleViewExistingDocument = () => {
    if (existingDocument) {
      if (existingDocument.documentType === 'link') {
        window.open(existingDocument.externalLink, '_blank');
      } else {
        // Open file via document service using the correct endpoint
        const baseURL = process.env.REACT_APP_API_URL || 'https://care360-backend.onrender.com';
        const fileUrl = `${baseURL}/documents/file/${existingDocument._id}`;
        window.open(fileUrl, '_blank');
      }
    }
  };

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {isVietnamese ? 'Nhập dữ liệu Điện tâm đồ' : 'EKG Study Entry'}
          </Title>
          <ImagingLanguageToggle onLanguageChange={changeLanguage} />
        </div>
        
        {/* EKG Document Section */}
        <Card title="EKG Document" style={{ marginBottom: 24 }}>
          {/* Show existing document info if editing */}
          {existingDocument && (
            <Alert
              message="Existing EKG Document"
              description={
                <div>
                  <Text strong>{existingDocument.documentName}</Text>
                  <br />
                  <Text type="secondary">
                    {existingDocument.documentType === 'file'
                      ? `File: ${existingDocument.fileName}`
                      : `Link: ${existingDocument.externalLink}`}
                  </Text>
                  <br />
                  <Button
                    type="link"
                    icon={existingDocument.documentType === 'file' ? <FileOutlined /> : <LinkOutlined />}
                    onClick={handleViewExistingDocument}
                    size="small"
                    style={{ paddingLeft: 0 }}
                  >
                    View Current EKG
                  </Button>
                </div>
              }
              type="info"
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item label="Document Type">
            <Radio.Group value={documentType} onChange={handleDocumentTypeChange}>
              <Radio.Button value="file">Upload File</Radio.Button>
              <Radio.Button value="link">External Link</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {documentType === 'file' ? (
            <Form.Item
              label="Upload EKG File"
              help="Supported formats: PDF, JPEG, PNG, DICOM (.dcm). Maximum size: 10MB"
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  Select EKG File
                </Button>
              </Upload>
              {uploadedFile && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    size="small"
                  >
                    Preview {uploadedFile.name}
                  </Button>
                </div>
              )}
            </Form.Item>
          ) : (
            <Form.Item
              label="External EKG Link"
              help="Enter the URL where the EKG file is hosted"
            >
              <Input
                value={externalLink}
                onChange={handleExternalLinkChange}
                placeholder="https://example.com/ekg-file.pdf"
                prefix={<LinkOutlined />}
              />
              {externalLink && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => window.open(externalLink, '_blank')}
                    size="small"
                  >
                    Preview Link
                  </Button>
                </div>
              )}
            </Form.Item>
          )}
        </Card>

        <Divider>
          {isVietnamese ? 'Dữ liệu Kết quả Điện tâm đồ' : 'EKG Interpretation Data'}
        </Divider>

        {/* EKG Parameters Section */}
        <Row gutter={[16, 16]}>
          {parameters.map(param => (
            <Col xs={24} sm={12} lg={8} key={param.key}>
              <Form.Item
                name={param.key}
                label={getTranslatedParameterName(param.key)}
                rules={[{ required: false }]}
              >
                {param.inputType === 'radio' ? (
                  <Radio.Group optionType="button" buttonStyle="solid">
                    {param.options && param.options.map(option => (
                      <Radio.Button key={option} value={option} style={{ marginBottom: 4 }}>
                        {getTranslatedOption(option)}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                ) : (
                  <Input placeholder={getTranslatedPlaceholder(param.key)} />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Form.Item style={{ marginTop: 24 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              {isVietnamese ? 'Hủy' : 'Cancel'}
            </Button>
            <Button type="primary" htmlType="submit">
              {isVietnamese ? 'Lưu Điện tâm đồ' : 'Save EKG Study'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EKGStudyInputForm;
